import { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { nl } from 'date-fns/locale'
import { getHistory, getSession, deleteSession, downloadSessionZip } from '../api/client'
import type { SendEmailResult } from '../api/client'
import { useStore } from '../store'
import { OverallStatusBadge } from './StatusBadge'
import { EmailConfirmModal } from './EmailConfirmModal'
import { buildEmailFiles } from '../utils/emailFilename'
import type { ReconciliationSessionSummary } from '../types'

export function HistoryTable() {
  const { t } = useTranslation()
  const { history, setHistory, setCurrentSession, setActiveView, setError, currentUser } = useStore()
  const [successId, setSuccessId] = useState<number | null>(null)
  const [modalSession, setModalSession] = useState<ReconciliationSessionSummary | null>(null)
  const [query, setQuery] = useState('')

  function buildModalFiles(s: ReconciliationSessionSummary) {
    return buildEmailFiles(s, {
      ordrsp: s.order_response_number !== null,
      desadv: s.desadv_number !== null,
      invoice: s.invoice_number !== null,
    })
  }

  useEffect(() => {
    getHistory()
      .then(setHistory)
      .catch((e) => setError(e instanceof Error ? e.message : t('history_table.error_load_history')))
  }, [setHistory, setError, t])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return history
    return history.filter((s) =>
      [
        s.project_number,
        s.supplier_name,
        s.invoice_number,
        s.order_response_number,
        s.desadv_number,
        s.ordrsp_buyers_order_number,
        s.buyer_name,
        currentUser?.is_admin ? s.uploaded_by : null,
      ]
        .filter(Boolean)
        .some((v) => v!.toLowerCase().includes(q))
    )
  }, [history, query, currentUser])

  const handleOpen = async (id: number) => {
    try {
      const session = await getSession(id)
      setCurrentSession(session)
      setActiveView('result')
    } catch (e) {
      setError(e instanceof Error ? e.message : t('history_table.error_load_session'))
    }
  }

  const handleSent = (id: number, _result: SendEmailResult) => {
    setModalSession(null)
    setSuccessId(id)
    setTimeout(() => setSuccessId(null), 4000)
  }

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('history_table.confirm_delete'))) return
    try {
      await deleteSession(id)
      setHistory(history.filter((s) => s.id !== id))
    } catch (e) {
      setError(e instanceof Error ? e.message : t('history_table.error_delete'))
    }
  }

  if (history.length === 0) {
    return (
      <div className="text-center text-gray-500 dark:text-gray-400 py-16">
        {t('history_table.empty_state')}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">🔍</span>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('history_table.search_placeholder')}
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        {query && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {t('history_table.filter_count', { filtered: filtered.length, total: history.length })}
          </span>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden overflow-x-auto">
        {modalSession && (
          <EmailConfirmModal
            sessionId={modalSession.id}
            files={buildModalFiles(modalSession)}
            hasPdf={modalSession.has_pdf}
            onSent={(result) => handleSent(modalSession.id, result)}
            onCancel={() => setModalSession(null)}
          />
        )}
        {filtered.length === 0 ? (
          <div className="text-center text-gray-500 dark:text-gray-400 py-10 text-sm">
            {t('history_table.no_results', { query })}
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">{t('history_table.col_id')}</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">{t('history_table.col_date')}</th>
                {currentUser?.is_admin && (
                  <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">{t('history_table.col_user')}</th>
                )}
                <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">{t('history_table.col_project')}</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">{t('history_table.col_order_number')}</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">{t('history_table.col_supplier')}</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">{t('history_table.col_customer')}</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">{t('history_table.col_ordrsp')}</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">{t('history_table.col_desadv')}</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">{t('history_table.col_invoice')}</th>
                <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-400">{t('history_table.col_lines')}</th>
                <th className="px-3 py-2 text-center font-medium text-gray-600 dark:text-gray-400">{t('history_table.col_status')}</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {filtered.map((s) => (
                <tr key={s.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <td className="px-3 py-2 text-gray-400 dark:text-gray-500 font-mono whitespace-nowrap">#{s.id}</td>
                  <td className="px-3 py-2 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    {format(new Date(s.created_at), 'd MMM yyyy HH:mm', { locale: nl })}
                  </td>
                  {currentUser?.is_admin && (
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400 font-mono">{s.uploaded_by}</td>
                  )}
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{s.project_number ?? '—'}</td>
                  <td className="px-3 py-2 font-mono text-gray-600 dark:text-gray-400 whitespace-nowrap">
                    {s.ordrsp_buyers_order_number ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{s.supplier_name ?? '—'}</td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{s.buyer_name ?? '—'}</td>
                  <td className="px-3 py-2 font-mono text-gray-600 dark:text-gray-400 whitespace-nowrap">
                    {s.order_response_number ?? '—'}
                  </td>
                  <td className="px-3 py-2 font-mono text-gray-600 dark:text-gray-400 whitespace-nowrap">
                    {s.desadv_number ?? '—'}
                  </td>
                  <td className="px-3 py-2 font-mono text-gray-600 dark:text-gray-400">
                    {s.invoice_number ?? '—'}
                    {s.sent_or_ref && (
                      <div className="mt-0.5 text-green-600 dark:text-green-400 font-mono" title={t('history_table.sent_tooltip', { ref: s.sent_or_ref })}>
                        ✓ OR{s.sent_or_ref}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">{s.line_count}</td>
                  <td className="px-3 py-2 text-center">
                    <OverallStatusBadge status={s.overall_status} />
                  </td>
                  <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                    <button
                      onClick={() => handleOpen(s.id)}
                      className="text-blue-600 dark:text-blue-400 hover:underline text-xs font-medium"
                    >
                      {t('history_table.btn_view')}
                    </button>
                    <button
                      onClick={() => downloadSessionZip(s.id)}
                      className="text-gray-500 dark:text-gray-400 hover:underline text-xs font-medium"
                    >
                      {t('history_table.btn_download_xml')}
                    </button>
                    <button
                      onClick={() => setModalSession(s)}
                      className="inline-flex items-center gap-1 text-indigo-600 dark:text-indigo-400 hover:underline text-xs font-medium"
                    >
                      {successId === s.id ? (
                        <span className="text-green-600 dark:text-green-400">{t('history_table.btn_sent_success')}</span>
                      ) : t('history_table.btn_mail')}
                    </button>
                    <button
                      onClick={() => handleDelete(s.id)}
                      className="text-red-400 dark:text-red-400 hover:underline text-xs"
                    >
                      {t('history_table.btn_delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
